import type { TFunction } from 'i18next'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { formatPhone } from 'src/utils/phone.util'
import { TBatchImportLogResult } from './batch-import.type'
import { getBatchImportRowReport } from './batch-import-report.util'

type TParams = {
  t: TFunction
  rowIds: number[]
  results: Record<number, TBatchImportLogResult>
  projectId: number
  locale: string
}

const PHONE_OR_NUMBER_LIKE = /^\+?[\d\s().-]+$/
const FORMULA_TRIGGER = /^[=+\-@\t\r]/

// Cells come from user-provided files: neutralize spreadsheet formulas (CSV injection), but keep phones intact
const escapeCsvCell = (value: string | number | undefined, separator: string): string => {
  const text = value === undefined ? '' : String(value)
  const safeText = FORMULA_TRIGGER.test(text) && !PHONE_OR_NUMBER_LIKE.test(text) ? `'${text}` : text
  return new RegExp(`["\\n\\r${separator}]`).test(safeText) ? `"${safeText.replace(/"/g, '""')}"` : safeText
}

// French spreadsheets expect ';' as column separator
export const getCsvSeparator = (locale: string) => (locale === 'fr' ? ';' : ',')

export const buildBatchImportCsv = ({ t, rowIds, results, projectId, locale }: TParams): string => {
  const separator = getCsvSeparator(locale)
  const header = [
    t('components.file-uploader.batch-import-csv-line'),
    t('components.file-uploader.batch-import-csv-remote-id'),
    t('components.file-uploader.batch-import-csv-lastname'),
    t('components.file-uploader.batch-import-csv-firstname'),
    t('components.file-uploader.batch-import-csv-birthdate'),
    t('components.file-uploader.batch-import-csv-phone'),
    t('components.file-uploader.batch-import-column-status'),
    t('components.file-uploader.batch-import-column-details'),
    t('components.file-uploader.batch-import-csv-patient-url'),
  ]

  const rows = rowIds.map((interopLogId, index) => {
    const result = results[interopLogId]
    const report = getBatchImportRowReport(t, result)
    const patient = result?.patient
    return [
      index + 1,
      patient?.remoteId,
      patient?.lastname,
      patient?.firstname,
      // birthdate is a calendar day: format it in UTC so the day doesn't shift with the viewer's timezone
      patient?.birthdate ? getFormattedDate(patient.birthdate, 'UTC') : undefined,
      patient?.phone ? formatPhone(patient.phone) || patient.phone : undefined,
      report.statusLabel,
      report.details.map((detail) => detail.text).join(' | '),
      result?.PatientId
        ? `${window.location.origin}/${locale}/${projectId}/medical/patients/${result.PatientId}`
        : undefined,
    ]
  })

  return [header, ...rows]
    .map((row) => row.map((cell) => escapeCsvCell(cell, separator)).join(separator))
    .join('\r\n')
}

export const downloadCsv = (filename: string, content: string) => {
  // BOM so spreadsheets open the file as UTF-8 (accents in names and messages)
  const blob = new Blob(['﻿', content], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  document.body.appendChild(link)
  link.href = url
  link.download = filename
  link.click()
  setTimeout(() => {
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
  }, 0)
}
